import React, { useEffect, useState } from 'react';
import {
  AddTeamMemberRequest,
  AddTeamRequest,
  TeamMemberResponse,
  TeamResponse
} from '../models/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { list, add, update, remove } from '../services/teamMember.service';
import { get as getTeam } from '../services/team.service';
import DateRender from '../components/DateRender';
import ModalForm from '../components/Modal';
import TeamsForm from '../components/TeamsForm';
import DeleteButton from '../components/DeleteButton';
import { Link, useParams } from 'react-router-dom';
import NotFound from './NotFound';
import TeamMembersForm from '../components/TeamMembersForm';
import BackButton from '../components/BackButton';
import Feedback from '../components/Feedback';

const TeamMembers: React.FC = () => {
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberResponse[]>([]);
  const [loading, setLoading] = useState<boolean | null>();
  const [errors, setErrors] = useState([] as string[]);
  const [loadMessage, setLoadMessage] = useState('');
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [isAddingTeamMemberLoading, setIsAddingTeamMemberLoading] =
    useState(false);
  const [addErrors, setAddErrors] = useState([] as string[]);
  const [editingTeamMember, setEditingTeamMember] =
    useState<TeamMemberResponse | null>(null);
  const [teamMemberDeletingReferences, setTeamMemberDeletingReferences] =
    useState([] as string[]);

  const { reference } = useParams();

  if (!reference) {
    return <NotFound />;
  }

  const getTeamMembers = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const [teamMembersResponse, teamResponse] = await Promise.all([
        list(reference),
        getTeam(reference)
      ]);
      if (
        teamMembersResponse.success &&
        teamResponse.success &&
        teamResponse.data
      ) {
        setTeamMembers(teamMembersResponse.data?.members || []);
        setTeam(teamResponse.data);
        setErrors([]);
      } else {
        setLoadMessage('');
        setErrors([...teamMembersResponse.errors, ...teamResponse.errors]);
      }
    } catch (error) {
      setErrors(['There was a problem loading the team']);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTeamMembers().catch(console.error);
  }, []);

  const submit = async (request: AddTeamMemberRequest) => {
    setIsAddingTeamMemberLoading(true);

    try {
      if (editingTeamMember) {
        const result = await update(
          reference,
          editingTeamMember.reference,
          request
        );
        if (result.success && result.data) {
          setLoadMessage('Team member updated');
          setIsAddingTeamMember(false);
          setEditingTeamMember(null);
          setAddErrors([]);
          await getTeamMembers();
          return true;
        } else {
          setAddErrors(result.errors);
          return false;
        }
      } else {
        const result = await add(reference, request);
        if (result.success && result.data) {
          setLoadMessage('Team member added');
          setIsAddingTeamMember(false);
          setAddErrors([]);
          await getTeamMembers();
          return true;
        } else {
          setAddErrors(result.errors);
          return false;
        }
      }
    } finally {
      setIsAddingTeamMemberLoading(false);
    }
  };

  const handleDelete = async (team: TeamMemberResponse) => {
    if (teamMemberDeletingReferences.includes(team.reference)) {
      return;
    }
    setTeamMemberDeletingReferences((p) => [...p, team.reference]);

    const result = await remove(reference, team.reference);
    if (result.success) {
      const teamsResponse = await list(reference);
      if (teamsResponse.success) {
        setTeamMembers(teamsResponse.data?.members || []);
        setLoadMessage('Team member deleted');
        setAddErrors([]);
      } else {
        setAddErrors(teamsResponse.errors);
      }
    } else {
      setLoadMessage('');
      setAddErrors(['There was a problem']);
    }
    setTeamMemberDeletingReferences((p) =>
      p.filter((q) => q !== team.reference)
    );
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      {loading === false && (
        <div className="content-body">
          <BackButton text="Back to teams" target="/teams" />
          <h2>{team?.name} members</h2>
          <Feedback message={loadMessage} type="success" />
          <Feedback messages={errors} type="error" />
          <button type="button" onClick={() => setIsAddingTeamMember(true)}>
            Add Team Member
          </button>
          {isAddingTeamMember && (
            <ModalForm>
              <TeamMembersForm
                errors={addErrors}
                isSubmitting={isAddingTeamMemberLoading}
                model={editingTeamMember}
                submit={submit}
                close={() => {
                  setIsAddingTeamMember(false);
                  setEditingTeamMember(null);
                  setAddErrors([]);
                }}
                buttonText={editingTeamMember ? 'Update Team' : 'Add Team'}
              />
            </ModalForm>
          )}
          {teamMembers.length === 0 && <p>No teams found</p>}
          {teamMembers.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created Date</th>
                  <th>Confirmed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((item) => (
                  <tr key={item.reference}>
                    <td>
                      {item.firstName || item.lastName
                        ? `${item.firstName} ${item.lastName} (${item.emailAddress})`
                        : `${item.emailAddress}`}
                    </td>
                    <td>
                      <DateRender dateString={item.createdDateUtc} />
                    </td>
                    <td>{item.isConfirmed ? 'Yes' : 'No'}</td>
                    <td>
                      {!item.isSelf && (
                        <>
                          <button
                            onClick={() => {
                              setEditingTeamMember(item);
                              setIsAddingTeamMember(true);
                            }}
                          >
                            Edit
                          </button>
                          <DeleteButton
                            isLoading={teamMemberDeletingReferences.includes(
                              item.reference
                            )}
                            onClick={() => handleDelete(item)}
                          >
                            Delete
                          </DeleteButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
};

export default TeamMembers;
